import React from 'react';
import PropTypes from 'prop-types';
import useVisibilityAwareInterval from 'src/hooks/useVisibilityAwareInterval';

const NavItemStatusContext = React.createContext({
  statuses: {},
  loading: false,
  refresh: () => Promise.resolve(),
});

export function useNavItemStatuses() {
  return React.useContext(NavItemStatusContext);
}

export function NavItemStatusProvider({ resolvers = [], refreshIntervalMs = 30 * 60 * 1000, dependencies = [], children }) {
  const [statuses, setStatuses] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const refreshIdRef = React.useRef(0);

  const refresh = React.useCallback(async () => {
    if (!resolvers || resolvers.length === 0) {
      setStatuses({});
      setLoading(false);
      return;
    }

    const refreshId = refreshIdRef.current + 1;
    refreshIdRef.current = refreshId;
    const resolverKeys = resolvers.map(({ key }) => key).filter(Boolean);

    // Clear stale statuses for keys we are about to resolve so old blocked states
    // do not keep the nav disabled while slower resolver requests are in flight.
    setStatuses((prev) => {
      const next = { ...prev };
      resolverKeys.forEach((key) => delete next[key]);
      return next;
    });

    setLoading(true);

    let pendingCount = 0;

    resolvers.forEach(({ key, resolve }) => {
      if (!key || typeof resolve !== 'function') {
        return;
      }

      pendingCount += 1;

      Promise.resolve()
        .then(() => resolve())
        .then((status) => {
          if (refreshIdRef.current !== refreshId) {
            return;
          }

          setStatuses((prev) => {
            const next = { ...prev };
            if (status) {
              next[key] = status;
            } else {
              delete next[key];
            }
            return next;
          });
        })
        .catch(() => {
          if (refreshIdRef.current !== refreshId) {
            return;
          }

          // If a resolver fails, surface an error status for that key
          setStatuses((prev) => ({
            ...prev,
            [key]: { type: 'error', tooltip: 'Unable to load status', blocked: false },
          }));
        })
        .finally(() => {
          if (refreshIdRef.current !== refreshId) {
            return;
          }

          pendingCount -= 1;
          if (pendingCount === 0) {
            setLoading(false);
          }
        });
    });

    if (pendingCount === 0) {
      setLoading(false);
    }
  }, [resolvers]);

  React.useEffect(() => {
    refresh();
    // We intentionally want to re-run when dependencies change (e.g., selected client)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, ...dependencies]);

  // Keep the refresh running even if the tab is backgrounded; these status checks are cheap and important.
  useVisibilityAwareInterval(refresh, refreshIntervalMs, {
    pauseOnHidden: false,
    runImmediatelyOnVisible: false,
  });

  const value = React.useMemo(
    () => ({
      statuses,
      loading,
      refresh,
    }),
    [statuses, loading, refresh],
  );

  return <NavItemStatusContext.Provider value={value}>{children}</NavItemStatusContext.Provider>;
}

NavItemStatusProvider.propTypes = {
  resolvers: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      resolve: PropTypes.func.isRequired,
    }),
  ),
  refreshIntervalMs: PropTypes.number,
  dependencies: PropTypes.array,
  children: PropTypes.node,
};

export default NavItemStatusContext;
